import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtEur, fmtDate, photoSrc } from "@/lib/format";
import { Wallet, Users, Building2, AlertTriangle, Plus, ArrowRight, Briefcase, CalendarClock, FileText, Eye } from "lucide-react";
import TenantWizard from "@/components/wizards/TenantWizard";
import ClientWizard from "@/components/wizards/ClientWizard";
import { PostponeTaskDialog } from "@/components/PostponeTaskDialog";
import { StaleComplaints } from "@/components/StaleComplaints";
import { DemoSeedCard } from "@/components/DemoSeedCard";
import { useAuth } from "@/context/AuthContext";

const StatCard = ({ icon: Icon, label, value, tint, tid, onClick }) => (
  <Card onClick={onClick} className="p-5 flex items-center gap-4 border-slate-200/80 border-t-2 border-t-[#C9A227] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer" data-testid={tid}>
    <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${tint}`}><Icon className="h-6 w-6" /></div>
    <div>
      <div className="text-2xl font-bold font-heading text-[#0F1E3D]">{value}</div>
      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">{label}</div>
    </div>
  </Card>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [hiddenMods, setHiddenMods] = useState([]);
  const [tw, setTw] = useState(false);
  const [cw, setCw] = useState(false);
  const [postponeTask, setPostponeTask] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [seedKey, setSeedKey] = useState(0);

  const load = () => api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  const loadTasks = () => api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {});
  useEffect(() => {
    load();
    loadTasks();
    api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
    api.get("/dashboard/expiring-contracts").then((r) => setExpiring(r.data)).catch(() => {});
    api.get("/catalog").then((r) => setHiddenMods(r.data.hidden_modules || [])).catch(() => {});
  }, [seedKey]);

  const DUNNING = { herinnering_1: "1e herinnering", herinnering_2: "2e herinnering", aanmaning: "Aanmaning" };
  const statusCounts = {};
  const dunningCounts = {};
  tenants.forEach((t) => {
    (t.statuses || []).forEach((s) => { statusCounts[s] = (statusCounts[s] || 0) + 1; });
    if (t.dunning_stage) dunningCounts[t.dunning_stage] = (dunningCounts[t.dunning_stage] || 0) + 1;
  });

  const today = new Date().toISOString().slice(0, 10);
  const noPay = hiddenMods.includes("tab-betalingen");
  const todayTasks = tasks.filter((t) => t.followup_date <= today);

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-[11px] tracking-[0.25em] font-semibold text-[#C9A227]">OVERZICHT</p>
          <h1 className="font-heading text-3xl font-bold text-[#0F1E3D]">Dashboard</h1>
          <p className="text-slate-500">Overzicht van openstaande betalingen en op te volgen huurders.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-[#C9A227]/70 text-[#0F1E3D] hover:bg-[#C9A227]/10" onClick={() => setCw(true)} data-testid="dashboard-add-client-button"><Briefcase className="h-4 w-4 mr-2 text-[#C9A227]" /> Opdrachtgever</Button>
          <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => setTw(true)} data-testid="dashboard-add-tenant-button"><Plus className="h-4 w-4 mr-2" /> Nieuwe debiteur</Button>
        </div>
      </div>

      {user?.role === "admin" && stats && stats.tenant_count === 0 && <DemoSeedCard onDone={() => setSeedKey((k) => k + 1)} />}

      {!hiddenMods.includes("complaints") && <StaleComplaints key={seedKey} />}

      {!hiddenMods.includes("tasks") && (
      <div className="bg-white rounded-xl border border-l-4 border-l-[#0F1E3D] p-5" data-testid="dashboard-tasks">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-semibold text-slate-800">Taken vandaag &amp; achterstallig</h2>
          <button className="text-sm text-[#0F1E3D] font-medium hover:text-[#C9A227] hover:underline" onClick={() => navigate("/app/taken")} data-testid="dashboard-tasks-all">Alle taken</button>
        </div>
        {todayTasks.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-sm" data-testid="dashboard-tasks-empty">Geen taken voor vandaag. 🎉</div>
        ) : (
          <div className="space-y-2">
            {todayTasks.slice(0, 6).map((t) => (
              <div key={t.id}
                className="flex items-center justify-between gap-2 p-2.5 rounded-lg border hover:bg-slate-50" data-testid={`dashboard-task-${t.id}`}>
                <div className="min-w-0 cursor-pointer flex-1" onClick={() => t.tenant_id ? navigate(`/app/huurders/${t.tenant_id}`) : navigate(`/app/opdrachtgevers/${t.client_id}`)}>
                  <div className="text-sm font-medium text-slate-800">{t.tenant_name || t.client_name || "—"}</div>
                  <div className="text-xs text-slate-500 truncate">{t.outcome}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs ${t.followup_date < new Date().toISOString().slice(0,10) ? "text-red-600" : "text-amber-600"}`}>{t.followup_date === new Date().toISOString().slice(0,10) ? "vandaag" : fmtDate(t.followup_date)}</span>
                  <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => setPostponeTask(t)} data-testid={`dashboard-task-postpone-${t.id}`}><CalendarClock className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      <div className={`grid grid-cols-1 sm:grid-cols-2 ${noPay ? "" : "lg:grid-cols-4"} gap-4`}>
        {!noPay && <StatCard icon={Wallet} label="Totaal openstaand" value={fmtEur(stats?.total_outstanding)} tint="bg-red-100 text-red-600" tid="stat-outstanding" onClick={() => navigate("/app/openstaand")} />}
        {!noPay && <StatCard icon={AlertTriangle} label="Achterstallige debiteuren" value={stats?.overdue_count ?? "—"} tint="bg-amber-100 text-amber-600" tid="stat-overdue" onClick={() => navigate("/app/openstaand")} />}
        <StatCard icon={Users} label="Debiteuren" value={stats?.tenant_count ?? "—"} tint="bg-[#0F1E3D] text-[#C9A227]" tid="stat-tenants" onClick={() => navigate("/app/huurders")} />
        <StatCard icon={Building2} label="Panden" value={stats?.property_count ?? "—"} tint="bg-[#C9A227]/15 text-[#0F1E3D]" tid="stat-properties" onClick={() => navigate("/app/panden")} />
      </div>

      {!noPay && expiring.length > 0 && (
        <Card className="p-6 border-slate-200/80 shadow-sm" data-testid="dashboard-expiring-contracts">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-slate-800 flex items-center gap-2"><CalendarClock className="h-5 w-5 text-amber-600" /> Aflopende contracten</h2>
            <span className="text-sm text-slate-400">{expiring.length} contract(en)</span>
          </div>
          <div className="space-y-2">
            {expiring.map((c) => (
              <div key={c.id} onClick={() => navigate(`/app/huurders/${c.tenant_id}`)} data-testid={`expiring-row-${c.id}`}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 cursor-pointer border border-transparent hover:border-slate-200 transition-all gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{c.tenant_name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><FileText className="h-3 w-3 shrink-0" /> {({ huur: "Huur", lening: "Lening", factuur: "Factuur" }[c.kind] || c.kind)}{c.ovk_number ? ` · ${c.ovk_number}` : ""} · einddatum {fmtDate(c.end_date)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.file_url && <Button size="sm" variant="outline" className="h-8 border-[#C9A227]/70 text-[#0F1E3D] hover:bg-[#C9A227]/10" onClick={(e) => { e.stopPropagation(); window.open(photoSrc(c.file_url), "_blank"); }} data-testid={`expiring-doc-${c.id}`}><Eye className="h-3.5 w-3.5 mr-1" /> Contract</Button>}
                  <Badge className={c.expired ? "bg-red-600 text-white hover:bg-red-600" : "bg-amber-500 text-white hover:bg-amber-500"} data-testid={`expiring-badge-${c.id}`}>
                    {c.expired ? `Verlopen op ${fmtDate(c.end_date)}` : `Verloopt over ${c.days_left} dag${c.days_left === 1 ? "" : "en"}`}
                  </Badge>
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {(Object.keys(statusCounts).length > 0 || Object.keys(dunningCounts).length > 0) && (
        <Card className="p-6 border-slate-200/80 shadow-sm" data-testid="dashboard-status-overview">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-[#0F1E3D]">Statusoverzicht debiteuren</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("/app/huurders")}>Alle debiteuren <ArrowRight className="h-4 w-4 ml-1" /></Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(dunningCounts).map(([k, n]) => (
              <button key={k} onClick={() => navigate("/app/huurders")} data-testid={`dashboard-dunning-${k}`}
                className="px-3 py-1.5 rounded-full text-sm bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors">
                {DUNNING[k] || k} <span className="font-bold ml-1">{n}</span>
              </button>
            ))}
            {Object.entries(statusCounts).map(([s, n]) => (
              <button key={s} onClick={() => navigate(`/app/huurders?status=${encodeURIComponent(s)}`)} data-testid={`dashboard-status-${s}`}
                className="px-3 py-1.5 rounded-full text-sm bg-[#0F1E3D]/5 text-[#0F1E3D] border border-[#C9A227]/50 hover:bg-[#C9A227]/15 transition-colors">
                {s} <span className="font-bold ml-1">{n}</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {!noPay && <Card className="p-6 border-slate-200/80 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold text-slate-800">Vandaag opvolgen — achterstallige debiteuren</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate("/app/huurders")}>Alle debiteuren <ArrowRight className="h-4 w-4 ml-1" /></Button>
        </div>
        {(!stats?.overdue_tenants || stats.overdue_tenants.length === 0) ? (
          <div className="text-center py-10 text-slate-400">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-40" />
            Geen achterstallige betalingen. Alles op orde! 🎉
          </div>
        ) : (
          <div className="space-y-2">
            {stats.overdue_tenants.map((t) => (
              <div key={t.id} onClick={() => navigate(`/app/huurders/${t.id}`)} data-testid={`overdue-row-${t.id}`}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 cursor-pointer border border-transparent hover:border-slate-200 transition-all">
                <div>
                  <div className="font-semibold text-slate-800">{t.first_name} {t.last_name}</div>
                  <div className="text-xs text-slate-500">{t.property_label || t.naw_city}</div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{t.overdue_count} termijn(en) te laat</Badge>
                  <span className="font-bold text-slate-800">{fmtEur(t.outstanding)}</span>
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>}

      <TenantWizard open={tw} onOpenChange={setTw} onSaved={() => load()} />
      <ClientWizard open={cw} onOpenChange={setCw} onSaved={() => load()} />
      {postponeTask && <PostponeTaskDialog task={postponeTask} onClose={() => setPostponeTask(null)} onDone={() => { setPostponeTask(null); loadTasks(); }} />}
    </div>
  );
}
